import React, { useCallback, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Box, Button, Chip, Divider, IconButton, Menu, MenuItem, TextField, Tooltip, Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import QuizIcon from "@mui/icons-material/Quiz";
import { toast } from "react-toastify";

import Langs, { Localize } from "../../app/lang/langs";
import {
    VLearnEmptyState, VLearnTypeIcon, VLEARN_TYPE_META, formatVLearnDuration,
    vlearnSurface, vlearnType
} from "./vlearnUi";
import LessonEditorDialog from "./LessonEditorDialog";
import ConfirmDialog from "../popups/ConfirmDialog";
import {
    deleteVLearnLesson, deleteVLearnSection, getVLearnCourse, moveVLearnLesson,
    reorderLessonsLocal, reorderSectionsLocal, reorderVLearnLessons,
    reorderVLearnSections, saveVLearnSection
} from "../../containers/vlearn/vlearnSlice";

/**
 * V-Learning — the "Content" tab: the sections of a course and the lessons inside them.
 *
 * ── Why buttons rather than drag and drop ────────────────────────────────────────────
 *
 * Reordering is two arrows per row. `react-dnd` is already a dependency and the scheduler
 * uses it, but a curriculum is a nested list — lessons inside sections, both reorderable —
 * and a nested drag surface is the hardest kind to get right, the hardest to use on a phone,
 * and unreachable from the keyboard without building a second control anyway. Two arrows are
 * usable everywhere, need no library, and say exactly what they do.
 *
 * ── Why a reorder writes twice ───────────────────────────────────────────────────────
 *
 * `reorderSectionsLocal` / `reorderLessonsLocal` apply the new order to the store first, then
 * the thunk persists it. The server answers with a count, not the new list, so without the
 * local move the row would snap back to where it was until the next full read.
 *
 * ── Why most mutations end in a refetch ──────────────────────────────────────────────
 *
 * Adding, deleting and moving have no local reducer, on purpose: each of them changes
 * `lessons_total` and therefore every enrolment's percentage, which is recomputed
 * server-side. Guessing at the result locally is how the sidebar and the dashboard start
 * disagreeing, so the course is read back instead.
 */

/** See the identical note in CourseEditor: two thunk families, two error shapes. */
const serverMessage = (payload) => {
    const error = payload && payload.responseError
    if (!error) return null
    const data = error.data || (error.response && error.response.data)
    return (data && data.error) || null
}

export default function CurriculumEditor({ sections, idCourse, onEditQuiz }) {

    const dispatch = useDispatch()
    const lang = useSelector((state) => state.settings.lang)

    /** Inline section edits, held locally until the field loses focus. */
    const [drafts, setDrafts] = useState({})
    /** `{ kind: 'section' | 'lesson', target }`, or null while nothing is being confirmed. */
    const [confirming, setConfirming] = useState(null)
    /** `{ idSection, idLesson }` — idLesson null means "a new lesson in this section". */
    const [lessonDialog, setLessonDialog] = useState(null)
    /** The anchor and lesson for the "move to another section" menu. */
    const [moveMenu, setMoveMenu] = useState(null)

    const reload = useCallback(
        () => dispatch(getVLearnCourse({ id_course: idCourse })), [dispatch, idCourse])

    /** Reports one mutation, preferring the server's own sentence over a generic failure. */
    const report = useCallback((response, successKey) => {
        const data = response.payload && response.payload.responseData
        if (data && data.status === 1) {
            if (successKey) toast.success(Localize(successKey, lang))
            return true
        }
        toast.error(serverMessage(response.payload) || Localize("VLEARN_ACTION_FAILED", lang))
        return false
    }, [lang])

    /**
     * Reorder requests, serialised per list and coalesced to the latest.
     *
     * These are arrow buttons, so three quick clicks on "move up" used to fire three
     * overlapping requests. Each one sends a complete order, so whichever the server finishes
     * last is the order that sticks — and it is not necessarily the last one clicked. The
     * store had already applied every move locally, so the author was left looking at an
     * order the database did not have, with nothing to hint at it until the next full read.
     *
     * A stale-response guard would not have helped: the responses carry only a count, and the
     * damage is the server applying writes out of order, not the client reading them out of
     * order. So only one request per list is allowed in flight, and any clicks made while it
     * is running collapse into a single follow-up carrying the newest order — intermediate
     * states are not worth a round trip when the author has already moved past them.
     *
     * Keyed per list (the section list, and each section's lessons) because those touch
     * disjoint rows and must not block each other.
     */
    const reorderQueuesRef = useRef({})
    const queueReorder = useCallback((key, thunk, payload) => {
        const queues = reorderQueuesRef.current
        const slot = queues[key] || (queues[key] = { inFlight: false, pending: null })

        if (slot.inFlight) {
            slot.pending = payload
            return
        }

        const send = async (body) => {
            slot.inFlight = true
            const response = await dispatch(thunk(body))
            slot.inFlight = false

            const next = slot.pending
            slot.pending = null
            if (next) {
                send(next)
                return
            }
            // Only reported once the queue is empty: a failure mid-burst would otherwise
            // toast for an order the author has already superseded. On failure the store is
            // ahead of the database, so the course is read back to resynchronise.
            const data = response.payload && response.payload.responseData
            if (!data || data.status !== 1) {
                toast.error(serverMessage(response.payload) || Localize("VLEARN_ACTION_FAILED", lang))
                reload()
            }
        }

        send(payload)
    }, [dispatch, lang, reload])

    const draftOf = (section) => drafts[section.id] || {
        title: section.title || '', description: section.description || ''
    }

    const setDraft = (section, changes) => setDrafts(prev => ({
        ...prev,
        [section.id]: { ...draftOf(section), ...changes }
    }))

    const clearDraft = (idSection) => setDrafts(prev => {
        const next = { ...prev }
        delete next[idSection]
        return next
    })

    /**
     * Persists an inline section edit when the field loses focus.
     *
     * Blur rather than a debounce: a section title is a short, deliberate edit made once,
     * unlike the description on the Details tab which is written over minutes. Saving on
     * blur means one request per edit and no timer to flush.
     */
    const commitSection = async (section) => {
        const draft = draftOf(section)
        const title = String(draft.title || '').trim()
        const description = draft.description || ''
        const unchanged = title === (section.title || '')
            && description === (section.description || '')
        if (unchanged) return clearDraft(section.id)
        if (!title) {
            // Mirrors `saveSection`'s own refusal. The client's job here is to explain it
            // where the author is looking; the server's is to guarantee it.
            toast.error(Localize("VLEARN_SECTION_TITLE_REQUIRED", lang))
            return clearDraft(section.id)
        }
        const response = await dispatch(saveVLearnSection({
            id_course: idCourse, id_section: section.id, title, description
        }))
        clearDraft(section.id)
        if (report(response)) reload()
    }

    const onAddSection = async () => {
        // Created with a placeholder name and renamed in place. One click to a visible row
        // beats a modal that asks for a title before the author can see where it lands.
        const response = await dispatch(saveVLearnSection({
            id_course: idCourse, title: Localize("VLEARN_SECTION_NEW_TITLE", lang)
        }))
        if (report(response)) reload()
    }

    const onMoveSection = (index, delta) => {
        const ordered = [...sections]
        const target = index + delta
        if (target < 0 || target >= ordered.length) return
        const [moved] = ordered.splice(index, 1)
        ordered.splice(target, 0, moved)
        dispatch(reorderSectionsLocal(ordered))
        queueReorder('sections', reorderVLearnSections,
            { id_course: idCourse, ids: ordered.map(s => s.id) })
    }

    const onMoveLesson = (section, index, delta) => {
        const ordered = [...(section.lessons || [])]
        const target = index + delta
        if (target < 0 || target >= ordered.length) return
        const [moved] = ordered.splice(index, 1)
        ordered.splice(target, 0, moved)
        dispatch(reorderLessonsLocal({ idSection: section.id, lessons: ordered }))
        queueReorder(`section:${section.id}`, reorderVLearnLessons,
            { id_section: section.id, ids: ordered.map(l => l.id) })
    }

    const onMoveToSection = async (idSection) => {
        const lesson = moveMenu && moveMenu.lesson
        setMoveMenu(null)
        if (!lesson) return
        const response = await dispatch(moveVLearnLesson({
            id_lesson: lesson.id, id_section: idSection
        }))
        if (report(response, "VLEARN_LESSON_MOVED")) reload()
    }

    const onConfirmed = async () => {
        const pending = confirming
        setConfirming(null)
        if (!pending) return
        if (pending.kind === 'section') {
            const response = await dispatch(deleteVLearnSection({
                id_course: idCourse, id_section: pending.target.id
            }))
            if (report(response, "VLEARN_SECTION_DELETED")) reload()
        } else {
            const response = await dispatch(deleteVLearnLesson({ id_lesson: pending.target.id }))
            if (report(response, "VLEARN_LESSON_DELETED")) reload()
        }
    }

    const lessonInDialog = useMemo(() => {
        if (!lessonDialog || !lessonDialog.idLesson) return null
        for (const section of (sections || [])) {
            const found = (section.lessons || []).find(l => l.id === lessonDialog.idLesson)
            if (found) return found
        }
        return null
    }, [lessonDialog, sections])

    const iconButton = (labelKey, icon, onClick, disabled) => {
        const label = Localize(labelKey, lang)
        const control = (
            <IconButton size="small" onClick={onClick} disabled={disabled} aria-label={label}
                sx={{
                    // These are the move / edit / delete controls on a curriculum row, and
                    // `size="small"` makes each of them a 30px square sitting beside the next.
                    // Under a fingertip that is a delete where an edit was meant.
                    minWidth: 44, minHeight: 44,
                    '&:focus-visible': {
                        outline: '2px solid var(--orange, rgb(255,144,21))', outlineOffset: '1px'
                    }
                }}>
                {icon}
            </IconButton>
        )
        // The span is required around a disabled button: it fires no pointer events, so the
        // tooltip would never open and the control would go silent.
        return (
            <Tooltip title={label} arrow>
                <span>{control}</span>
            </Tooltip>
        )
    }

    if (!(sections || []).length) {
        return (
            <Box sx={vlearnSurface.card}>
                <VLearnEmptyState
                    icon={<MenuBookOutlinedIcon />}
                    title={<Langs str="VLEARN_NO_SECTION_TITLE" />}
                    description={<Langs str="VLEARN_NO_SECTION_DESC" />}
                    action={
                        <Button variant="contained" startIcon={<AddIcon />} onClick={onAddSection}
                            sx={{
                                textTransform: 'none',
                                backgroundColor: 'var(--orange, rgb(255,144,21))',
                                '&:hover': { backgroundColor: '#e07f0d' }
                            }}>
                            <Langs str="VLEARN_ADD_FIRST_SECTION" />
                        </Button>
                    }
                />
            </Box>
        )
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

            {sections.map((section, sectionIndex) => {
                const draft = draftOf(section)
                const lessons = section.lessons || []
                return (
                    <Box key={section.id} component="section" sx={vlearnSurface.card}>
                        <Box sx={{ padding: { xs: '12px', sm: '16px 18px' } }}>

                            <Box sx={{
                                display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap'
                            }}>
                                <Chip size="small" label={sectionIndex + 1}
                                    sx={{
                                        fontWeight: 700, fontSize: '0.72rem',
                                        backgroundColor: 'rgba(255, 144, 21, 0.14)', color: '#B25E00'
                                    }} />
                                <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
                                    {/* The heading IS the input. An author renaming a section
                                        should not have to find an edit affordance first. */}
                                    <TextField
                                        variant="standard" fullWidth
                                        value={draft.title}
                                        onChange={(event) => setDraft(section, { title: event.target.value })}
                                        onBlur={() => commitSection(section)}
                                        inputProps={{
                                            maxLength: 190,
                                            'aria-label': Localize("VLEARN_SECTION_TITLE", lang)
                                        }}
                                        InputProps={{
                                            disableUnderline: true,
                                            sx: { ...vlearnType.sectionTitle, fontSize: '1rem' }
                                        }}
                                    />
                                    <TextField
                                        variant="standard" fullWidth multiline
                                        placeholder={Localize("VLEARN_SECTION_DESCRIPTION_PLACEHOLDER", lang)}
                                        value={draft.description}
                                        onChange={(event) => setDraft(section, { description: event.target.value })}
                                        onBlur={() => commitSection(section)}
                                        inputProps={{ 'aria-label': Localize("VLEARN_SECTION_DESCRIPTION", lang) }}
                                        InputProps={{ disableUnderline: true, sx: vlearnType.body }}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    {iconButton('VLEARN_MOVE_UP',
                                        <ArrowUpwardIcon sx={{ fontSize: 17 }} />,
                                        () => onMoveSection(sectionIndex, -1), sectionIndex === 0)}
                                    {iconButton('VLEARN_MOVE_DOWN',
                                        <ArrowDownwardIcon sx={{ fontSize: 17 }} />,
                                        () => onMoveSection(sectionIndex, 1),
                                        sectionIndex === sections.length - 1)}
                                    {iconButton('VLEARN_DELETE_SECTION',
                                        <DeleteOutlineIcon sx={{ fontSize: 17 }} />,
                                        () => setConfirming({ kind: 'section', target: section }))}
                                </Box>
                            </Box>

                            <Divider sx={{ my: 1.5 }} />

                            {lessons.length ? (
                                <Box component="ul" sx={{
                                    listStyle: 'none', margin: 0, padding: 0,
                                    display: 'flex', flexDirection: 'column', gap: '6px'
                                }}>
                                    {lessons.map((lesson, lessonIndex) => {
                                        const duration = formatVLearnDuration(lesson.duration_seconds)
                                        const meta = VLEARN_TYPE_META[lesson.type] || VLEARN_TYPE_META.text
                                        return (
                                            <Box component="li" key={lesson.id} sx={{
                                                display: 'flex', alignItems: 'center', gap: 1,
                                                flexWrap: 'wrap',
                                                padding: '8px 10px', borderRadius: '10px',
                                                border: '1px solid rgba(0,0,0,0.06)',
                                                transition: 'background-color .15s ease',
                                                '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' },
                                                '@media (prefers-reduced-motion: reduce)': {
                                                    transition: 'none'
                                                }
                                            }}>
                                                <VLearnTypeIcon type={lesson.type} />
                                                <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
                                                    <Typography component="h3" sx={vlearnType.cardTitle}>
                                                        {lesson.title}
                                                    </Typography>
                                                    <Typography sx={vlearnType.meta}>
                                                        <Langs str={meta.key} />
                                                        {duration ? ` · ${duration}` : ''}
                                                    </Typography>
                                                </Box>

                                                {Number(lesson.is_mandatory) ? (
                                                    <Tooltip arrow
                                                        title={Localize("VLEARN_MANDATORY_TIP", lang)}>
                                                        <Chip size="small"
                                                            label={<Langs str="VLEARN_MANDATORY" />}
                                                            sx={{
                                                                fontSize: '0.68rem', height: 20,
                                                                backgroundColor: 'rgba(255, 144, 21, 0.14)',
                                                                color: '#B25E00'
                                                            }} />
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip arrow
                                                        title={Localize("VLEARN_OPTIONAL_TIP", lang)}>
                                                        <Chip size="small" variant="outlined"
                                                            label={<Langs str="VLEARN_OPTIONAL" />}
                                                            sx={{ fontSize: '0.68rem', height: 20 }} />
                                                    </Tooltip>
                                                )}

                                                {lesson.type === 'quiz' && lesson.id_quiz ? (
                                                    <Button size="small"
                                                        startIcon={<QuizIcon sx={{ fontSize: 15 }} />}
                                                        onClick={() => onEditQuiz && onEditQuiz(lesson.id_quiz)}
                                                        sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                                                        <Langs str="VLEARN_EDIT_QUIZ" />
                                                    </Button>
                                                ) : null}

                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    {iconButton('VLEARN_MOVE_UP',
                                                        <ArrowUpwardIcon sx={{ fontSize: 16 }} />,
                                                        () => onMoveLesson(section, lessonIndex, -1),
                                                        lessonIndex === 0)}
                                                    {iconButton('VLEARN_MOVE_DOWN',
                                                        <ArrowDownwardIcon sx={{ fontSize: 16 }} />,
                                                        () => onMoveLesson(section, lessonIndex, 1),
                                                        lessonIndex === lessons.length - 1)}
                                                    {iconButton('VLEARN_MOVE_TO_SECTION',
                                                        <DriveFileMoveOutlinedIcon sx={{ fontSize: 16 }} />,
                                                        (event) => setMoveMenu({
                                                            anchor: event.currentTarget, lesson
                                                        }),
                                                        sections.length < 2)}
                                                    {iconButton('VLEARN_EDIT_LESSON',
                                                        <EditOutlinedIcon sx={{ fontSize: 16 }} />,
                                                        () => setLessonDialog({
                                                            idSection: section.id, idLesson: lesson.id
                                                        }))}
                                                    {iconButton('VLEARN_DELETE_LESSON',
                                                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />,
                                                        () => setConfirming({ kind: 'lesson', target: lesson }))}
                                                </Box>
                                            </Box>
                                        )
                                    })}
                                </Box>
                            ) : (
                                <Typography sx={{ ...vlearnType.meta, padding: '4px 2px 10px' }}>
                                    <Langs str="VLEARN_SECTION_NO_LESSON" />
                                </Typography>
                            )}

                            <Button size="small" startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                                onClick={() => setLessonDialog({ idSection: section.id, idLesson: null })}
                                sx={{ textTransform: 'none', fontSize: '0.8rem', mt: 1 }}>
                                <Langs str="VLEARN_ADD_LESSON" />
                            </Button>
                        </Box>
                    </Box>
                )
            })}

            <Box>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={onAddSection}
                    sx={{ textTransform: 'none' }}>
                    <Langs str="VLEARN_ADD_SECTION" />
                </Button>
            </Box>

            {/* Moving a lesson between sections is a different operation from reordering —
                the server validates the destination belongs to the same course — so it gets
                its own control rather than a drag that could mean either. */}
            <Menu open={!!moveMenu} anchorEl={moveMenu && moveMenu.anchor}
                onClose={() => setMoveMenu(null)}>
                {(sections || [])
                    .filter(section => !moveMenu || !moveMenu.lesson
                        || section.id !== moveMenu.lesson.id_section)
                    .map(section => (
                        <MenuItem key={section.id} onClick={() => onMoveToSection(section.id)}
                            sx={{ fontSize: '0.85rem' }}>
                            {section.title}
                        </MenuItem>
                    ))}
            </Menu>

            {/* Both messages name what is destroyed. A lesson's uploaded video goes with the
                lesson row, because the row is the only record of where the bytes are — the
                author has to know that before they click, not after. */}
            <ConfirmDialog
                open={!!confirming}
                danger
                title={<Langs str={confirming && confirming.kind === 'section'
                    ? 'VLEARN_DELETE_SECTION_TITLE' : 'VLEARN_DELETE_LESSON_TITLE'} />}
                message={<Langs str={confirming && confirming.kind === 'section'
                    ? 'VLEARN_DELETE_SECTION_BODY' : 'VLEARN_DELETE_LESSON_BODY'} />}
                confirmLabel={<Langs str="VLEARN_DELETE" />}
                onConfirm={onConfirmed}
                onCancel={() => setConfirming(null)}
            >
                {confirming ? (
                    <Typography sx={{ ...vlearnType.cardTitle, mt: 1.5 }}>
                        {confirming.target.title}
                    </Typography>
                ) : null}
            </ConfirmDialog>

            <LessonEditorDialog
                open={!!lessonDialog}
                lesson={lessonInDialog}
                idSection={lessonDialog ? lessonDialog.idSection : null}
                idCourse={idCourse}
                onClose={() => setLessonDialog(null)}
                onSaved={reload}
            />
        </Box>
    )
}
